import {
  BlobReader,
  type Entry,
  ERR_BAD_FORMAT,
  ERR_CENTRAL_DIRECTORY_NOT_FOUND,
  ERR_EOCDR_LOCATOR_ZIP64_NOT_FOUND,
  ERR_EOCDR_NOT_FOUND,
  ERR_EXTRAFIELD_ZIP64_NOT_FOUND,
  ERR_SPLIT_ZIP_FILE,
  ERR_UNSUPPORTED_FORMAT,
  ZipReader,
} from "@zip.js/zip.js";
import {
  archiveLimitReason,
  isZipFileName,
  planZipUnpack,
  ZIP_UNPACK_LIMITS,
  type ZipEntryInfo,
  type ZipKeptReason,
  type ZipUnpackBudget,
  type ZipUnpackLimits,
} from "@hightouch/lib/llm/zip-uploads/rules";
import prettyBytes from "pretty-bytes";
import type { ComposerFile } from "src/components/llm/utils/file-processing";
import { captureException } from "src/lib/monitoring";

const ZIP_LOCAL_FILE_SIGNATURE = [0x50, 0x4b, 0x03, 0x04];
const S_IFMT = 0o170000;
const S_IFLNK = 0o120000;

// zip.js throws these when the bytes are not a zip it can list, as opposed to a failure to read the file at all.
const NOT_A_ZIP_ERRORS = new Set([
  ERR_BAD_FORMAT,
  ERR_CENTRAL_DIRECTORY_NOT_FOUND,
  ERR_EOCDR_LOCATOR_ZIP64_NOT_FOUND,
  ERR_EOCDR_NOT_FOUND,
  ERR_EXTRAFIELD_ZIP64_NOT_FOUND,
  ERR_SPLIT_ZIP_FILE,
  ERR_UNSUPPORTED_FORMAT,
]);

/** An archive's central directory as the browser read it, without any file contents. */
export type ZipDirectory =
  | { kind: "unreadable" }
  | {
      kind: "zip";
      /** Stops at one past `maxEntries`, since a longer directory is never unpacked. */
      entryCount: number;
      entries: ZipEntryInfo[];
    };

/** Why a zip can't join the message. */
export type ZipAttachmentProblem = { code: "unreadable" } | ZipKeptReason;

/** One attachment on the message, in the order the message sends them. */
export type ZipCheckAttachment = {
  fileName: string;
  sizeBytes: number | undefined;
  /** Absent for a file that is not a zip, and for a zip the browser has no bytes for or could not read. */
  zipDirectory?: ZipDirectory;
};

// Keyed by the picked file so a zip is read once, and its directory is known when later attachments are checked.
const directories = new WeakMap<Blob, ZipDirectory>();

/**
 * Reads only the central directory, so a large archive is checked without reading its files.
 * Rejects only when the file itself can't be read; bytes that aren't a zip the worker can open resolve as unreadable.
 */
export async function readZipDirectory(
  archive: Blob,
  { maxEntries }: Pick<ZipUnpackLimits, "maxEntries">,
): Promise<ZipDirectory> {
  const head = new Uint8Array(
    await archive.slice(0, ZIP_LOCAL_FILE_SIGNATURE.length).arrayBuffer(),
  );
  if (!ZIP_LOCAL_FILE_SIGNATURE.every((byte, index) => head[index] === byte)) {
    return { kind: "unreadable" };
  }

  const reader = new ZipReader(new BlobReader(archive));
  try {
    const entries: ZipEntryInfo[] = [];
    for await (const entry of reader.getEntriesGenerator()) {
      // The worker checks the entry count before it lists entries, so a crowded archive reports that first.
      if (entries.length === maxEntries) {
        return { kind: "zip", entryCount: maxEntries + 1, entries };
      }
      entries.push(toZipEntryInfo(entry));
    }
    return { kind: "zip", entryCount: entries.length, entries };
  } catch (error) {
    if (error instanceof Error && NOT_A_ZIP_ERRORS.has(error.message)) {
      return { kind: "unreadable" };
    }
    throw error;
  } finally {
    await reader.close();
  }
}

/** Reads the directory of each zip the browser has the bytes of. A read that fails for any other reason is reported and leaves the zip unchecked. */
export async function loadZipDirectories(
  files: ComposerFile[],
  limits: ZipUnpackLimits = ZIP_UNPACK_LIMITS,
): Promise<void> {
  await Promise.all(
    files.map(async ({ fileName, file }) => {
      if (!file || !isZipFileName(fileName) || directories.has(file)) return;
      try {
        directories.set(file, await readZipDirectory(file, limits));
      } catch (error) {
        captureException(error);
      }
    }),
  );
}

/** The directory `loadZipDirectories` read for this attachment, if any. */
export function zipDirectoryOf(file: ComposerFile): ZipDirectory | undefined {
  return file.file ? directories.get(file.file) : undefined;
}

/** Applies the worker's unpack rules to each zip, with the file and byte budget shared across them in attachment order. */
export function zipAttachmentProblems(
  attachments: ZipCheckAttachment[],
  limits: ZipUnpackLimits = ZIP_UNPACK_LIMITS,
): Map<number, ZipAttachmentProblem> {
  const direct = attachments.filter(
    (attachment) => !isZipFileName(attachment.fileName),
  );
  const budget: ZipUnpackBudget = {
    files: limits.maxMembers - direct.length,
    bytes:
      limits.maxTotalBytes -
      direct.reduce((sum, attachment) => sum + (attachment.sizeBytes ?? 0), 0),
  };
  const problems = new Map<number, ZipAttachmentProblem>();

  for (const [
    index,
    { fileName, sizeBytes, zipDirectory },
  ] of attachments.entries()) {
    if (!isZipFileName(fileName) || !zipDirectory) continue;
    if (zipDirectory.kind === "unreadable") {
      problems.set(index, { code: "unreadable" });
      continue;
    }
    const archiveReason = archiveLimitReason(
      { sizeBytes: sizeBytes ?? 0, entryCount: zipDirectory.entryCount },
      limits,
    );
    if (archiveReason) {
      problems.set(index, archiveReason);
      continue;
    }
    const plan = planZipUnpack(zipDirectory.entries, { limits, budget });
    if (plan.outcome === "keep") {
      problems.set(index, plan.reason);
      continue;
    }
    budget.files -= plan.planned.length;
    budget.bytes -= plan.bytes;
  }
  return problems;
}

/** The error for a selection that would leave a zip on the message packed, or null when every zip still fits. */
export function zipSelectionError({
  existing,
  incoming,
  limits = ZIP_UNPACK_LIMITS,
}: {
  existing: ZipCheckAttachment[];
  incoming: ZipCheckAttachment[];
  limits?: ZipUnpackLimits;
}): string | null {
  // A zip already on the message keeps its problem, so only a problem the selection adds refuses it.
  const known = zipAttachmentProblems(existing, limits);
  const attachments = [...existing, ...incoming];
  for (const [index, problem] of zipAttachmentProblems(attachments, limits)) {
    const zip = attachments[index];
    if (zip && !known.has(index)) {
      return describeZipProblem(zip.fileName, problem, limits);
    }
  }
  return null;
}

/** The error for a zip that can't be attached. */
export function describeZipProblem(
  zipName: string,
  problem: ZipAttachmentProblem,
  limits: ZipUnpackLimits = ZIP_UNPACK_LIMITS,
): string {
  switch (problem.code) {
    case "unreadable":
      return `"${zipName}" can't be read as a zip file. Check that it isn't damaged, or attach a different file.`;
    case "archive_too_large":
      return `"${zipName}" is over the ${formatBytes(problem.maxBytes)} limit for a zip. Split it into smaller zips and try again.`;
    case "too_many_entries":
      return `"${zipName}" has more than ${formatCount(problem.maxEntries)} files and folders. Split it into smaller zips and try again.`;
    case "too_many_files": {
      const has = `"${zipName}" has ${formatFiles(problem.files)}`;
      const limit = `A message can hold ${formatFiles(limits.maxMembers)}`;
      if (problem.available >= limits.maxMembers) {
        return `${has}. ${limit}, so remove some files from the zip.`;
      }
      const room =
        problem.available === 0
          ? "no more fit"
          : `only ${formatCount(problem.available)} more fit`;
      return `${has}, and ${room} on this message. ${limit}, so remove some files from the zip or attach fewer files.`;
    }
    case "too_many_bytes": {
      const has = `"${zipName}" unzips to ${formatBytes(problem.bytes)}`;
      const limit = `A message can hold ${formatBytes(limits.maxTotalBytes)}`;
      if (problem.available >= limits.maxTotalBytes) {
        return `${has}. ${limit}, so remove some files from the zip.`;
      }
      const room =
        problem.available === 0
          ? "this message has no room left"
          : `only ${formatBytes(problem.available)} more fit on this message`;
      return `${has}, and ${room}. ${limit}, so remove some files from the zip or attach fewer files.`;
    }
  }
}

const formatCount = (count: number) => count.toLocaleString("en-US");

const formatFiles = (count: number) =>
  `${formatCount(count)} ${count === 1 ? "file" : "files"}`;

// Binary units match the 1 GiB and 2 GiB limits the composer already shows.
const formatBytes = (bytes: number) => prettyBytes(bytes, { binary: true });

function toZipEntryInfo(entry: Entry): ZipEntryInfo {
  return {
    // The worker's reader turns Windows backslashes into forward slashes.
    path: entry.filename.replace(/\\/g, "/"),
    compressedSize: entry.compressedSize,
    uncompressedSize: entry.uncompressedSize,
    compressionMethod: entry.compressionMethod,
    encrypted: entry.encrypted,
    symbolicLink: ((entry.externalFileAttributes >>> 16) & S_IFMT) === S_IFLNK,
  };
}
